import {useParams} from 'react-router-dom';

import {
    useDefectSignatureQuery,
    useUpdateDefectSignatureMutation,
} from './useDefectSignaturesQuery';
import {useTestObservationsInfiniteQuery} from '../../test-observations';
import {DefectStatus} from '../types';

// FNP-904 — one defect, plus the observations that rolled into it.
//
// ═══ WHY THE CONTRIBUTING OBSERVATIONS ARE MATCHED CLIENT-SIDE ═══
//
// A TestObservation does NOT store its fingerprint — the signature is derived
// from it by deployed code (FNP-903), deliberately, so the normalization can be
// changed without rewriting history. There is therefore no
// listObservationsByFingerprint query and no index to add one against without
// backfilling a derived value onto immutable evidence rows.
//
// So this narrows by `actionCode` SERVER-SIDE (a real filter on the list query)
// and then keeps the rows whose normalized error matches — the same comparison
// the engine makes. The actionCode filter is what keeps this cheap: it is a
// component of the fingerprint, so no observation outside that partition can
// possibly belong to this signature.
//
// The honest limitation, surfaced in the UI rather than hidden: this matches
// within the LOADED observation pages. A signature whose contributing rows are
// older than the loaded window shows fewer than its occurrenceCount, so the
// panel states both numbers instead of implying the list is complete.
export const useDefectSignature = () => {
    const {id} = useParams();
    const signatureId = id ? decodeURIComponent(id) : '';

    const query = useDefectSignatureQuery(signatureId);
    const record = query.data;

    // Server-side narrowing to this defect's action. Enabled only once the
    // signature has loaded — without an actionCode this would fetch the whole
    // observation table.
    const observationsQuery = useTestObservationsInfiniteQuery(
        record?.actionCode ? {actionCode: record.actionCode} : {},
    );

    const observations = (observationsQuery.data?.pages?.flatMap(page => page?.items ?? []) ?? [])
        .filter(Boolean);

    // Match on the normalized error the signature was built from. Falls back to
    // the whole actionCode group when the row carries no normalized form, which
    // is the honest answer for a legacy row rather than showing nothing.
    const contributingObservations = record?.normalizedError
        ? observations.filter(observation => {
            if (!observation.error) {
                return false;
            }
            // The engine's normalization is not re-implemented here — that would
            // be a second copy of the fingerprint rule, free to drift from the
            // deployed one. Instead this compares the SHAPE loosely: an
            // observation belongs if the normalized error's literal segments
            // (the parts between placeholders) all appear in its raw error.
            const segments = record.normalizedError!
                .split(/<[a-z]+>/i)
                .map(segment => segment.trim())
                .filter(segment => segment.length > 8);

            if (segments.length === 0) {
                return true;
            }
            return segments.every(segment => observation.error!.includes(segment));
        })
        : observations;

    const updateMutation = useUpdateDefectSignatureMutation();

    return {
        id: signatureId,
        record,
        loading: query.isLoading,
        isError: query.isError,
        contributingObservations,
        observationsLoading: observationsQuery.isLoading,
        // Stated so the panel can say "showing N of M" rather than implying
        // completeness — see the header.
        occurrenceCount: record?.occurrenceCount ?? 0,
        acknowledge: () => {
            if (!record) {
                return;
            }
            updateMutation.mutate({
                                      id: record.id,
                                      status: DefectStatus.ACKNOWLEDGED,
                                  });
        },
        fileAs: (jiraKey: string) => {
            if (!record) {
                return;
            }
            updateMutation.mutate({
                                      id: record.id,
                                      status: DefectStatus.FILED,
                                      jiraKey: jiraKey,
                                  });
        },
        updating: updateMutation.isPending,
        updateError: updateMutation.isError,
        reload: () => query.refetch(),
    };
};
